/**
 * Lógica compartida para armar el mensaje de pedido a WhatsApp (Checkout + asistente).
 */

import { logStructured } from '@/lib/networkLog';
import { getProductIncludedSummary } from '@/lib/productComposition';
import { buildGoogleMapsAddressUrl } from '@/lib/checkout/deliveryAddress';

export function parseCashAmountInput(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return null;
  const n = Number(s.replace(/\./g, '').replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n);
}

export function buildWhatsappOrderRef() {
  const raw = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
    .replace(/[^a-z0-9]/gi, '')
    .toUpperCase();
  return `#${(raw + 'XXXXXX').slice(0, 6)}`;
}

export function formatBusinessDeliveryTime(b) {
  if (!b) return null;
  const m = b.delivery_time_minutes;
  if (typeof m === 'number' && m > 0) return `~${m} min`;
  const s = b.delivery_time && String(b.delivery_time).trim();
  return s || null;
}

export function formatBusinessPrepTime(b) {
  if (!b) return null;
  const p = b.preparation_time_minutes;
  if (typeof p === 'number' && p > 0) return `~${p} min`;
  const ps = b.preparation_time && String(b.preparation_time).trim();
  return ps || null;
}

export function getEstimatedTimeForWhatsapp(business, serviceType, tableNumber = '') {
  if (serviceType === 'delivery') {
    const m = business?.delivery_time_minutes;
    if (typeof m === 'number' && m > 0) return `${m} min aprox.`;
    const s = business?.delivery_time && String(business.delivery_time).trim();
    if (s) return `${s} aprox.`;
    return '30-45 min aprox.';
  }
  const p = business?.preparation_time_minutes;
  if (typeof p === 'number' && p > 0) return `${p} min aprox.`;
  const ps = business?.preparation_time && String(business.preparation_time).trim();
  if (ps) return `${ps} aprox.`;
  return '15-20 min aprox.';
}

/**
 * Devuelve el parámetro `text` ya encodeado para wa.me/?text=
 */
export function buildEncodedWhatsAppOrderText({
  business,
  cart,
  getCartTotal,
  customerName = '',
  customerPhone = '',
  serviceType = 'delivery',
  tableNumber = '',
  address = '',
  markerPosition = null,
  orderNotes = '',
  tip = 0,
  paymentMethod = 'efectivo',
  /** 'card' | 'mercadopago' | null — solo si paymentMethod === 'online_manual' */
  onlineManualKind = null,
  cashAmount = '',
  proofUrl = null,
  deliveryQuote = null,
  referralInfo = null,
  benefitsSnapshot = null,
  appliedPremioLabel = null,
  clubSummaryLine = '',
  /** URL absoluta; se agrega al final del mensaje (solo checkout con seguimiento). */
  customerTrackingAbsoluteUrl = null,
}) {
  const orderRef = buildWhatsappOrderRef();

  const clienteConBeneficiosOHistorial = () => {
    const bp = benefitsSnapshot;
    const ri = referralInfo;
    if (bp?.enabled && bp?.customer_found) {
      const orders = Number(bp.total_orders) || 0;
      const avail = Number(bp.available_rewards) || 0;
      if (orders > 0 || avail > 0) return true;
    }
    if (ri?.customer_found) {
      const rc = Number(ri.referrals_count) || 0;
      const ar = Number(ri.available_referral_rewards) || 0;
      if (rc > 0 || ar > 0 || ri.referred_by) return true;
    }
    return false;
  };

  let serviceLabel = '';
  if (serviceType === 'delivery') {
    serviceLabel = 'Delivery';
  } else if (serviceType === 'retiro') {
    serviceLabel = 'Retiro en local';
  } else {
    serviceLabel = `Mesa N°${tableNumber}`;
  }

  let message = `🛒 *PEDIDO NUEVO* - ${business.name}\n`;
  message += `*Pedido:* ${orderRef}\n`;
  message += `*Tiempo estimado:* ${getEstimatedTimeForWhatsapp(business, serviceType, tableNumber)}\n\n`;

  if (customerName) {
    message += `*Cliente:* ${customerName}\n`;
  }
  if (customerPhone) {
    message += `*Teléfono:* ${customerPhone}\n`;
  }
  if (clienteConBeneficiosOHistorial()) {
    message += `⭐ Cliente con beneficios\n`;
  }
  message += `*Servicio:* ${serviceLabel}\n\n`;

  message += '*Productos:*\n\n';
  (cart || []).forEach((item) => {
    const itemQuantity = Number(item.quantity) || 1;
    if (item.isClubReward) {
      const lbl = item.clubRewardLabel ? ` — ${item.clubRewardLabel}` : '';
      message += `• ${item.product.name}${lbl} x${itemQuantity} — 🎁 Premio canjeado ($0)\n`;
    } else {
      const itemPrice = Number(item.price) || 0;
      const itemTotal = itemPrice * itemQuantity;
      message += `• ${item.product.name} x${itemQuantity} — $${itemTotal.toLocaleString()}\n`;
      const includedRaw =
        getProductIncludedSummary(item.product) ||
        item.product?.includes ||
        item.product?.included_items ||
        item.product?.comes_with ||
        item.product?.comesWith ||
        item.product?.included ||
        item.product?.sideDish;
      const includedText = Array.isArray(includedRaw)
        ? includedRaw.map((x) => String(x ?? '').trim()).filter(Boolean).join(', ')
        : includedRaw != null
          ? String(includedRaw).trim()
          : '';
      if (includedText) {
        message += `   Incluye: ${includedText}\n`;
      }
    }

    if (item.addOns && item.addOns.length > 0) {
      message += `   *Agregados:*\n`;
      item.addOns.forEach((addon) => {
        const addonQty = Number(addon.quantity) || 1;
        const addonPrice = Number(addon.price) || 0;
        const addonTotal = addonPrice * addonQty;
        if (addonQty > 1) {
          message += `      • ${addon.name} x${addonQty} ($${addonPrice.toLocaleString()} c/u = $${addonTotal.toLocaleString()})\n`;
        } else {
          message += `      • ${addon.name} (+$${addonPrice.toLocaleString()})\n`;
        }
      });
    }

    if (item.removedIngredients && item.removedIngredients.length > 0) {
      message += `   Sin: ${item.removedIngredients.join(', ')}\n`;
    }

    message += '\n';
  });

  const notesTrim = String(orderNotes || '').trim();
  if (notesTrim) {
    message += `*Indicaciones / hora deseada:*\n${notesTrim}\n\n`;
  }

  const tipAmount = Number(tip) || 0;
  const subtotalAmount = Number(getCartTotal()) || 0;
  const deliveryAmount =
    typeof deliveryQuote?.delivery_fee === 'number' ? deliveryQuote.delivery_fee : 0;
  const totalAmount = subtotalAmount + deliveryAmount + tipAmount;

  message += `*Resumen:*\n`;
  message += `*Subtotal:* $${subtotalAmount.toLocaleString()}\n`;
  if (deliveryAmount > 0) {
    message += `*Delivery:* $${deliveryAmount.toLocaleString()}\n`;
  }
  if (tipAmount > 0) {
    message += `*Propina:* $${tipAmount.toLocaleString()}\n`;
  }
  message += `*Total:* $${totalAmount.toLocaleString()}\n\n`;

  message += `*Pago:*\n`;
  if (paymentMethod === 'efectivo') {
    message += 'Efectivo';
    const cashParsed = parseCashAmountInput(cashAmount);
    if (cashParsed != null) {
      message += `\nPaga con: $${cashParsed.toLocaleString()}`;
      const vuelto = cashParsed - totalAmount;
      if (vuelto > 0) {
        message += `\nVuelto: $${vuelto.toLocaleString()}`;
      }
    }
  } else if (paymentMethod === 'transferencia') {
    message += 'Transferencia bancaria';
    if (proofUrl) {
      message += `\n📎 *Comprobante:* ${proofUrl}`;
    }
  } else if (paymentMethod === 'online_manual') {
    if (onlineManualKind === 'mercadopago') {
      if (
        business?.mercadopago_online === true ||
        business?.mp_oauth_connected === true
      ) {
        message += 'Mercado Pago — pago online';
      } else {
        message += 'MercadoPago (el local enviará link de pago)';
      }
    } else {
      message += 'Tarjeta / Débito / Crédito en el local';
    }
  } else {
    message += String(paymentMethod || '—');
  }

  if (serviceType === 'delivery' && String(address || '').trim()) {
    message += `\n\n*Dirección:* ${String(address).trim()}`;
  }
  const mapsUrl = buildGoogleMapsAddressUrl({ address, location: markerPosition });
  if (serviceType === 'delivery' && mapsUrl) {
    message += `\n*Ubicación:* ${mapsUrl}`;
  }

  const footerBits = [];
  if (appliedPremioLabel) {
    footerBits.push(`🎁 *Premio aplicado:* ${appliedPremioLabel}`);
  }
  if (clubSummaryLine) {
    footerBits.push(clubSummaryLine);
  }
  if (footerBits.length > 0) {
    message += `\n\n${footerBits.join('\n')}`;
  }

  const trackUrl = customerTrackingAbsoluteUrl != null ? String(customerTrackingAbsoluteUrl).trim() : '';
  if (trackUrl) {
    message += `\n\n📍 Puedes seguir tu pedido aquí: ${trackUrl}`;
  }

  return encodeURIComponent(message);
}

/** Decodifica el texto generado por `buildEncodedWhatsAppOrderText` (p. ej. para copiar al portapapeles). */
export function decodeWhatsAppEncodedText(encoded) {
  if (encoded == null || encoded === '') return '';
  try {
    return decodeURIComponent(encoded);
  } catch {
    return '';
  }
}

/**
 * Extrae dígitos de teléfono Chile/E.164 desde texto libre o URL wa.me / api.whatsapp.com.
 * @param {unknown} raw
 * @returns {string}
 */
function digitsFromWhatsappCandidate(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  const fromQuery = s.match(/[?&]phone=(\d{8,15})/i);
  if (fromQuery) return fromQuery[1];
  const fromPath = s.match(/(?:wa\.me|api\.whatsapp\.com\/send)\/?(?:phone\/)?(\d{8,15})/i);
  if (fromPath) return fromPath[1];
  const digits = s.replace(/[^0-9]/g, '');
  if (digits.length >= 8 && digits.length <= 15) return digits;
  return '';
}

/**
 * Dígitos del WhatsApp del local para wa.me / tel: (whatsapp → whatsapp_url → phone).
 * @param {object|null|undefined} business
 * @returns {string}
 */
export function getBusinessWhatsappDigits(business) {
  const fromWhatsapp = digitsFromWhatsappCandidate(business?.whatsapp);
  if (fromWhatsapp) return fromWhatsapp;
  const fromUrl = digitsFromWhatsappCandidate(business?.whatsapp_url);
  if (fromUrl) return fromUrl;
  return digitsFromWhatsappCandidate(business?.phone);
}

export function buildWhatsAppOrderUrl(business, encodedText) {
  const whatsappNumber = getBusinessWhatsappDigits(business);
  if (!whatsappNumber) return null;
  return `https://wa.me/${whatsappNumber}?text=${encodedText}`;
}

/**
 * Abre wa.me en nueva pestaña; si el popup está bloqueado o falla, usa `location.assign`.
 * No pasar `noopener` en windowFeatures: con eso el browser suele devolver null aunque la
 * pestaña abrió, y el fallback navegaba la SPA entera a WhatsApp (doble navegación / UX rota).
 * @param {string|null|undefined} whatsappUrl
 * @param {{ businessId?: string, source?: string }} [ctx] — contexto para analytics (sin PII)
 * @returns {boolean} true si se disparó alguna navegación
 */
export function openWhatsAppUrl(whatsappUrl, ctx) {
  const businessId = ctx?.businessId;
  const source = ctx?.source;
  if (!whatsappUrl) {
    logStructured('analytics', {
      event: 'whatsapp_failed',
      businessId,
      source,
      reason: 'no_url',
    });
    console.warn('[checkout] WhatsApp: sin URL (número del local inválido o vacío)');
    return false;
  }
  logStructured('analytics', {
    event: 'whatsapp_opened',
    businessId,
    source,
  });
  try {
    const w = window.open(whatsappUrl, '_blank');
    if (w == null) {
      console.warn(
        '[checkout] WhatsApp: window.open devolvió null (popup bloqueado?); usando location.assign'
      );
      window.location.assign(whatsappUrl);
      return true;
    }
    try {
      w.opener = null;
    } catch {
      /* ignore */
    }
    return true;
  } catch (e) {
    console.warn('[checkout] WhatsApp: error en window.open; usando location.assign', e);
    window.location.assign(whatsappUrl);
    return true;
  }
}
